import React from 'react';
import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import type { PlanMarketValuation } from '../../models/MarketValuationTypes';
import { formatCurrency } from '../../utils/CurrencyFormat';
import MarketTechnicalsSection from './MarketTechnicalsSection';

interface Props {
  valuation: PlanMarketValuation | null;
  loading?: boolean;
  /** When true, show partner-scaled amounts instead of plan-level totals. */
  partnerView?: boolean;
  stockName?: string;
  compact?: boolean;
}

function displayAmounts(valuation: PlanMarketValuation, partnerView: boolean) {
  if (partnerView) {
    return {
      invested: valuation.partnerInvestedAmount ?? valuation.investedAmount,
      current: valuation.partnerCurrentValue ?? valuation.currentValue,
      pnl: valuation.partnerUnrealizedPnl ?? valuation.unrealizedPnl,
    };
  }
  return {
    invested: valuation.investedAmount,
    current: valuation.currentValue,
    pnl: valuation.unrealizedPnl,
  };
}

const MarketValuationPanel: React.FC<Props> = ({
  valuation,
  loading,
  partnerView = false,
  stockName,
  compact = false,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
        <Typography variant="body2" color="text.secondary">
          Loading market price…
        </Typography>
      </Paper>
    );
  }

  if (!valuation) {
    return null;
  }

  if (valuation.sharesHeld <= 0) {
    if (valuation.lastClosePrice != null && valuation.technicals) {
      return (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Market snapshot
              </Typography>
              {stockName && (
                <Typography variant="subtitle2" fontWeight={700}>
                  {stockName}
                </Typography>
              )}
              {valuation.nseCode && (
                <Chip label={valuation.nseCode} size="small" variant="outlined" sx={{ mt: 0.5 }} />
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                Last close (close|1)
              </Typography>
              <Typography variant="h5" fontWeight={800} color="primary.main">
                {formatCurrency(valuation.lastClosePrice)}
              </Typography>
            </Box>
          </Stack>
          <MarketTechnicalsSection
            technicals={valuation.technicals}
            lastClosePrice={valuation.lastClosePrice}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Record a purchase to see position value and unrealised P&amp;L.
          </Typography>
        </Paper>
      );
    }
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No shares held — market valuation appears after a purchase is recorded.
        </Typography>
      </Paper>
    );
  }

  if (!valuation.priceAvailable || valuation.lastClosePrice == null) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {valuation.nseCode
            ? `Latest close price unavailable for ${valuation.nseCode}.`
            : 'NSE symbol not set on this stock profile.'}
        </Typography>
      </Paper>
    );
  }

  const { invested, current, pnl } = displayAmounts(valuation, partnerView);
  const pnlPositive = pnl >= 0;
  const pnlColor = pnlPositive ? theme.palette.success.main : theme.palette.error.main;
  const pnlPct =
    invested > 0 ? ((pnl / invested) * 100).toFixed(2) : valuation.unrealizedPnlPct?.toFixed(2);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 2 : 2.5,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderColor: alpha(theme.palette.primary.main, 0.2),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Live valuation
          </Typography>
          {stockName && (
            <Typography variant="subtitle2" fontWeight={700}>
              {stockName}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            {valuation.nseCode && <Chip label={valuation.nseCode} size="small" variant="outlined" />}
            <Chip label={`${valuation.sharesHeld} shares`} size="small" color="primary" variant="outlined" />
          </Stack>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            Last close (NSE)
          </Typography>
          <Typography variant={compact ? 'h5' : 'h4'} fontWeight={800} color="primary.main" sx={{ lineHeight: 1.1 }}>
            {formatCurrency(valuation.lastClosePrice)}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {partnerView ? 'Your invested' : 'Cost basis (held)'}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(invested)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {partnerView ? 'Your current value' : 'Current value'}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(current)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {valuation.sharesHeld} × {formatCurrency(valuation.lastClosePrice)}
              {partnerView ? ' × your share' : ''}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha(pnlColor, 0.08),
              border: '1px solid',
              borderColor: alpha(pnlColor, 0.35),
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              {pnlPositive ? (
                <TrendingUp fontSize="small" sx={{ color: pnlColor }} />
              ) : (
                <TrendingDown fontSize="small" sx={{ color: pnlColor }} />
              )}
              <Typography variant="caption" color="text.secondary">
                Unrealised P&amp;L
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight={700} sx={{ color: pnlColor }}>
              {pnlPositive ? '+' : ''}
              {formatCurrency(pnl)}
            </Typography>
            {pnlPct != null && (
              <Typography variant="caption" sx={{ color: pnlColor }}>
                {pnlPositive ? '+' : ''}
                {pnlPct}%
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {valuation.technicals && (
        <MarketTechnicalsSection
          technicals={valuation.technicals}
          lastClosePrice={valuation.lastClosePrice}
        />
      )}

      {valuation.priceFetchedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Price from Tradient (latest daily close). Not live tick-by-tick LTP.
        </Typography>
      )}
    </Paper>
  );
};

export default MarketValuationPanel;
